// Store the currency at the time the report is run (moved inside component)
import { BarChart, Download, PlayArrow } from "@mui/icons-material";
import { ColDef, ICellRendererParams } from "ag-grid-community";
import "ag-grid-community/styles/ag-grid.css";
import "ag-grid-community/styles/ag-theme-alpine.css";
import dayjs from "dayjs";
import Highcharts from "highcharts";
import { last } from "lodash";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { MarkItViewApi } from "../../../../Apis/Apis";
import { CommodityType, ContractDate, ForecastDecileModel, ForecastDecileRowModel, ForecastModel, MarketViewProductModel, QuoteDateForecast, Unit } from "../../../../Generated/Mark-It-View-Api";
import { CurrencyHelper, EnumHelper } from "../../../../Helpers";
import { useLoadingState, useMtmDate } from "../../../../Hooks";
import { StoneXGrid, StoneXLoading, StoneXRow } from "../../../StoneX";
import MarkItViewProductSelector from "../../../StoneX/MarkItViewProductSelector.tsx/MarkItViewProductSelector";
import MarkItViewCommodityTypeSelector from "../../../StoneX/MarkItViewCommodityTypeSelector/MarkItViewCommodityTypeSelector";
import StoneXChart from "../../../StoneX/StoneXChart";
import StoneXMainPage from "../../../StoneX/StoneXMainPage/StoneXMainPage";
import { StoneXButton, StoneXContractMonthPicker, StoneXDatePicker, StoneXNumber, StoneXSelect, StoneXTextField, StoneXToggleButton } from "../../../StoneXMui";
import { useApplicationSelector, RootState } from "../../../../Redux/ReduxStore";
import { UserAccessState } from "../../../../Redux/Slices/UserAccessSlice";
import { exportForecastToPowerPoint } from "./exportForecastToPowerPoint";
import { buildDecileMatrixPalette, decileMatrixStyles } from "../DecileMatrixStyles";


export default function ForecastTool() {
  const [reportCurrency, setReportCurrency] = useState<string>('');
  const [reportUnit, setReportUnit] = useState<string>('');

  const userAccess = useApplicationSelector((state: RootState): UserAccessState => state.userAccess);
  const isEmployee = userAccess.isEmployee;
  const expirationsLoadingState = useLoadingState();

  const [quoteDate, setQuoteDate] = useState<Date | null>(useMtmDate());
  const [selectedProduct, setSelectedProduct] = useState<MarketViewProductModel | null>(null);
  const [selectedCommodityType, setSelectedCommodityType] = useState<CommodityType>(CommodityType.Favorites);
  
  const forecastLoadingState = useLoadingState();
  const [forecastHistory, setForecastHistory] = useState<QuoteDateForecast[]>([]);
  const [matrixTable, setMatrixTable] = useState<ForecastDecileModel | null>(null);
  const [selectedPercentiles, setSelectedPercentiles] = useState<Set<number>>(new Set());
  const quoteDateForecasts = useMemo<ForecastModel[]>(() => last(forecastHistory)?.forecasts ?? [], [forecastHistory]);

  const [includeHistory, setIncludeHistory] = useState<boolean>(isEmployee ? true : false);
  const [applyPpi, setApplyPpi] = useState<boolean>(true);
  const [monthsAhead, setMonthsAhead] = useState<number>(24);
  const [startMonth, setStartMonth] = useState<ContractDate | null>(null);

  const [viewMoreInTable, setViewMoreInTable] = useState<boolean>(false);

  const forecastChartRef = useRef<Highcharts.Chart | null>(null);

  const matrixConfig = useMemo(buildDecileMatrixPalette, []);

  function togglePercentile(index: number) {
    setSelectedPercentiles(prev => {
      const next = new Set(prev);
      if (next.has(index)) {
        next.delete(index);
      } else {
        next.add(index);
      }
      return next;
    });
  }

  useEffect(() => {
    if (!quoteDate) return;
    if (includeHistory) return; 
    if (!selectedProduct) return;
    
    MarkItViewApi.listMarketViewExpirationDates(expirationsLoadingState.setLoadingState, [selectedProduct.productId]).then((response) => {
      const contractDateToExpiration = response.data.productIdToContractDateToExpiration?.[`${selectedProduct.productId}`];
      if (contractDateToExpiration) {
        const quoteDateDayjs = dayjs(quoteDate);
        const entries = Object.entries(contractDateToExpiration)
          .map(([contractLabel, expirationDate]) => ({ contractLabel, expirationDate: dayjs(expirationDate) }))
          .filter(e => e.expirationDate.isAfter(quoteDateDayjs))
          .sort((a, b) => a.expirationDate.diff(b.expirationDate));

        console.log('entries', entries);

        if (entries.length > 0) {
          const nextContract = entries[0].contractLabel;
          const parsed = dayjs(nextContract, 'YYYY-MM');
          if (parsed.isValid()) {
            setStartMonth({ year: parsed.year(), month: parsed.month() + 1 });
          }
        }
      }
    });
  }, [quoteDate, includeHistory, selectedProduct]);

  function getForecat() {
    const quoteDateString = dayjs(quoteDate).format('L');
    const historyYears = isEmployee 
      ? includeHistory ? 4 : 0
      : 0;
    const startYear = !includeHistory && startMonth ? startMonth.year : undefined;
    const startMonthValue = !includeHistory && startMonth ? (startMonth.month !== null ? startMonth.month! + 1 : undefined) : undefined;
    MarkItViewApi.getForecastWithHistory(forecastLoadingState.setLoadingState, `${selectedProduct?.productId}`, quoteDateString, undefined, undefined, historyYears, startYear, startMonthValue, undefined, monthsAhead, applyPpi).then((response) => {
      setForecastHistory(response.data.rows ?? []);
      setMatrixTable(response.data.decileTable ?? null);
      // Capture the currency and unit at the time the report is run
      setReportCurrency(selectedProduct?.currency || '');
      setReportUnit(selectedProduct?.unit && selectedProduct.unit !== Unit.None ? selectedProduct.unit : '');
      const averages = response.data.averages;
      if (averages) {
        setAverageRow([{ contractDate: { label: 'Average' }, ...averages }]);
      } else {
        setAverageRow([]);
      }
    });
  }

  function handleExportPowerPoint() {
    exportForecastToPowerPoint({
      productName: selectedProduct?.name ?? 'Forecast',
      forecasts: quoteDateForecasts,
      matrixTable,
      selectedPercentiles,
      matrixConfig,
      viewMoreInTable,
      averageRow,
      chartRef: forecastChartRef.current,
    });
  }
  
  const gray4 = getComputedStyle(document.documentElement).getPropertyValue('--gray4').trim();

  const reportCurrencyUnit = useMemo(
    () => (reportCurrency && reportUnit ? `${reportCurrency}/${reportUnit}` : reportCurrency),
    [reportCurrency, reportUnit]
  );

  const columns = useMemo<ColDef[]>(() => {
    return [
      {headerName: 'Month', field: 'contractDate.label', flex: 1},
      {headerName: 'Settlements', field: 'settlementAverage', flex: 1, cellRenderer: (params: ICellRendererParams<ForecastModel>) => <StoneXNumber number={params.value} decimals={4} />, hide: !viewMoreInTable},
      {headerName: `Last ${60} Days`, field: 'recentAverage', flex: 1, cellRenderer: (params: ICellRendererParams<ForecastModel>) => <StoneXNumber number={params.value} decimals={4} />, hide: !viewMoreInTable},
      {headerName: 'Median', field: 'historicalMedian', flex: 1, cellRenderer: (params: ICellRendererParams<ForecastModel>) => <StoneXNumber number={params.value} decimals={4} />, hide: !viewMoreInTable},
      {headerName: 'Forecast', field: 'forecastPrice', flex: 1, cellRenderer: (params: ICellRendererParams<ForecastModel>) => <StoneXNumber number={params.value} decimals={4} />},
      {headerName: 'Market', field: 'marketPrice', flex: 1, cellRenderer: (params: ICellRendererParams<ForecastModel>) => <StoneXNumber number={params.value} decimals={4} />},
      {
        headerName: 'Forecast Spread',
        field: 'forecastSpread',
        flex: 1,
        cellRenderer: (params: ICellRendererParams<ForecastModel>) => {
          const market = params.data?.marketPrice ?? 0;
          const forecast = params.data?.forecastPrice ?? 0;
          const spread = forecast - market;
          return <StoneXNumber number={spread} decimals={4} colored />;
        },
      },
      {
        headerName: 'Annual',
        field: 'annualCurrency',
        flex: 1,
        valueGetter: () => reportCurrencyUnit,
        cellRenderer: (params: ICellRendererParams) => <span>{params.value}</span>,
      },
    ];
  }, [viewMoreInTable, reportCurrencyUnit]);

  // Patch the row data to always include annualCurrency for the grid
  const quoteDateForecastsWithAnnual = useMemo(() => {
    return quoteDateForecasts.map(row => ({ ...row, annualCurrency: reportCurrencyUnit }));
  }, [quoteDateForecasts, reportCurrencyUnit]);

  const [averageRow, setAverageRow] = useState<any[]>([]);

  const reportCurrencySymbol = useMemo(() => CurrencyHelper.getSymbol(reportCurrency), [reportCurrency]);

  const tableTopMenuItems = useMemo(() => {
    if (isEmployee) {
      return [{
        id: 'toggle-view',
        label: viewMoreInTable ? 'View less' : 'View more',
        onClick: () => { setViewMoreInTable(previous => !previous); }
      }];
    }
    return [];
  }, [isEmployee, viewMoreInTable]);

  const matrixPlotBands = useMemo<Highcharts.YAxisPlotBandsOptions[]>(() => {
    if (!matrixTable?.rows) return [];
    return matrixTable.rows
      .map((row: ForecastDecileRowModel, index: number) => ({ row, index }))
      .filter(({ index }: { index: number }) => selectedPercentiles.has(index))
      .map(({ row, index }: { row: ForecastDecileRowModel; index: number }) => ({
        from: row.lowValue,
        to: row.highValue,
        color: `${matrixConfig[index].color}${matrixConfig[index].chartOpacity}`,
        borderColor: `${matrixConfig[index].color}${matrixConfig[index].chartOpacity}`,
        borderWidth: 1,
        label: {
          text: row.label ?? '',
          style: { color: matrixConfig[index].textColor, fontWeight: '600', fontSize: '11px' },
        },
      }));
  }, [matrixTable, selectedPercentiles]);

  const forecastChartOptions = useMemo<Highcharts.Options>(() => ({
    title: { text: `${selectedProduct?.name} Forecast` },
    credits: {
      enabled: false,
    },
    xAxis: {
      categories: quoteDateForecasts.map(f => f.contractDate?.label ?? ""),
      title: { 
        text: "Month",
        style: {
            color: gray4,
            fontWeight: '700'
        } },
      labels: {
        style: {
          color: gray4,
          fontWeight: '700'
        }
      }
    },
    yAxis: {
      title: { 
        text: "Forecast",
        style: {
            color: gray4,
            fontWeight: '700'
        } },
      labels: {
        formatter: function (this: Highcharts.AxisLabelsFormatterContextObject) {
          return `${reportCurrencySymbol}${parseFloat(`${this.value}`).toFixed(2)}`;
        },
        style: {
          color: gray4,
          fontWeight: '700'
        }
      },
      plotBands: matrixPlotBands,
    },
    legend: { enabled: true },
    series: [
      {
        name: "Forecast",
        data: quoteDateForecasts.map(f => f.forecastPrice),
        type: "line",
        dashStyle: "Dash"
      },
      {
        name: "Market Price",
        data: quoteDateForecasts.map(f => f.marketPrice),
        type: "line"
      }
    ],
    tooltip: {
      shared: true,
      valueDecimals: 4,
      valuePrefix: reportCurrencySymbol
    },
    exporting: {
      buttons: {
        contextButton: {
          enabled: true,
          menuItems: [
            'downloadPNG',
            'downloadSVG',
          ]
        },
      },
    },
  }), [quoteDateForecasts, matrixPlotBands, reportCurrencySymbol]);
  
  const forecastHistoryChartOptions = useMemo<Highcharts.Options>(() => ({
    chart: {
      zoomType: 'x',
      panKey: 'shift', // Hold shift to pan
    },
    title: { text: `${selectedProduct?.name} Historical Averages` },
    credits: {
      enabled: false,
    },
    xAxis: {
      categories: forecastHistory.map(f => f.quoteDate),
      title: { 
        text: "Quote Date",
        style: {
            color: gray4,
            fontWeight: '700'
        } },
      labels: {
        style: {
          color: gray4,
          fontWeight: '700'
        },
        formatter: function (this: Highcharts.AxisLabelsFormatterContextObject) {
          return dayjs(this.value as string).format('L');
        }
      },
      scrollbar: { enabled: true },
    },
    yAxis: {
      title: { 
        text: "Historical Average",
        style: {
            color: gray4,
            fontWeight: '700'
        } },
      labels: {
        formatter: function (this: Highcharts.AxisLabelsFormatterContextObject) {
          return `${reportCurrencySymbol}${parseFloat(`${this.value}`).toFixed(2)}`;
        },
        style: {
          color: gray4,
          fontWeight: '700'
        }
      }
    },
    legend: { enabled: true },
    series: [
      {
        name: "Forecast Average",
        data: forecastHistory.map(f => f.forecastAverage),
        type: "line",
        dashStyle: "Dash"
      },
      {
        name: "Forward Average",
        data: forecastHistory.map(f => f.forwardAverage),
        type: "line"
      }
    ],
    tooltip: {
      shared: true,
      valueDecimals: 4,
      valuePrefix: reportCurrencySymbol
    },
    exporting: {
      buttons: {
        contextButton: {
          enabled: true,
          menuItems: [
            'downloadPNG',
            'downloadSVG',
          ]
        },
      },
    },
  }), [forecastHistory, selectedProduct, reportCurrencySymbol]);

  return (
    <StoneXMainPage>
      <StoneXRow>
        <h1>Forecast</h1>
      </StoneXRow>
      <StoneXRow>
        <StoneXDatePicker value={quoteDate!} onChange={setQuoteDate} label="Quote Date" />
        <MarkItViewCommodityTypeSelector
          value={selectedCommodityType}
          onChange={(x) => setSelectedCommodityType(x!)}
        />
        <MarkItViewProductSelector 
          onProductChange={setSelectedProduct}
          commodityType={selectedCommodityType}
          minProductHistoryYears={4}
          lastQuoteDateThresholdDays={5}
        />
        {!includeHistory && <StoneXContractMonthPicker disabled={expirationsLoadingState.isLoading()} value={startMonth} onChange={setStartMonth} label="Start Month" />}
        <StoneXTextField 
          label='Months Ahead'
          type='number' 
          min={1}
          max={36}
          value={monthsAhead}
          onUpdate={x => setMonthsAhead(parseInt(x) ?? 24)}
        />  
        {isEmployee && <StoneXToggleButton label="Include History" value={includeHistory} onChange={setIncludeHistory} />}
        <StoneXToggleButton label="Apply PPI" value={applyPpi} onChange={setApplyPpi} />
        <StoneXButton variant="outlined" startIcon={<PlayArrow />} disabled={!quoteDate || !selectedProduct || forecastLoadingState.isLoading()} onClick={getForecat}>Run</StoneXButton>
        {forecastLoadingState.isLoaded() && <StoneXButton className="animate-pop-in" variant="outlined" startIcon={<Download />} onClick={handleExportPowerPoint}>Export</StoneXButton>}
      </StoneXRow>

      <StoneXLoading show={forecastLoadingState.isLoading()}/>
      {forecastLoadingState.isLoaded() && <>
        <StoneXRow>
          <div style={{ display: 'flex', flex: 1, gap: '16px' }}>
            <div style={{flex: 1}}>
              <StoneXChart highcharts={Highcharts} options={forecastChartOptions} onChartReady={(chart) => { forecastChartRef.current = chart; }} />
            </div>
            {matrixTable && (
              <div style={decileMatrixStyles.container}>
                <h3 style={decileMatrixStyles.heading}>Price Matrix</h3>
                <table style={decileMatrixStyles.table}>
                  <tbody>
                    <tr>
                      <td></td>
                      <td></td>
                      <td style={{ ...decileMatrixStyles.headerCell, textAlign: 'right' }}>Annual{reportCurrencyUnit ? ` (${reportCurrencyUnit})` : ''}</td>
                    </tr>
                    <tr>
                      <td></td>
                      <td style={decileMatrixStyles.labelCell}>Mean</td>
                      <td style={decileMatrixStyles.valueCell}><StoneXNumber number={matrixTable.mean} decimals={4} /></td>
                    </tr>
                    <tr>
                      <td></td>
                      <td style={decileMatrixStyles.labelCell}>Median</td>
                      <td style={decileMatrixStyles.valueCell}><StoneXNumber number={matrixTable.median} decimals={4} /></td>
                    </tr>
                    {matrixTable.rows?.map((row: ForecastDecileRowModel, index: number) => {
                      const isSelected = selectedPercentiles.has(index);
                      return (
                        <tr
                          key={index}
                          onClick={() => togglePercentile(index)}
                          style={{
                            cursor: 'pointer',
                          }}
                        >
                          <td>
                             {isSelected && <BarChart style={{ fontSize: '16px', color: matrixConfig[index].color }} />}
                          </td>
                          <td style={{ ...decileMatrixStyles.labelCell, backgroundColor: matrixConfig[index].color, color: matrixConfig[index].tableTextColor }}>
                            <span style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                             
                              {row.label}
                            </span>
                          </td>
                          <td style={decileMatrixStyles.valueCell}>
                            <StoneXNumber number={row.lowValue} decimals={3} /> - <StoneXNumber number={row.highValue} decimals={3} />
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
                <p style={{ margin: '8px 0 0 0', fontSize: '11px', color: 'var(--helper-text-color)' }}>Click a percentile row to overlay it on the chart.</p>
              </div>
            )}
          </div>
        </StoneXRow>

        {/* Only show history chart if there is any history  */}
        {forecastHistory?.length > 1 &&
          <StoneXRow>
            <StoneXChart highcharts={Highcharts} options={forecastHistoryChartOptions} />
          </StoneXRow>
        }

        <StoneXGrid
          rows={quoteDateForecastsWithAnnual}
          columns={columns}
          hideGridOptions
          tableTopMenuItems={tableTopMenuItems}
          gridOptions={{ pinnedBottomRowData: averageRow }}
        />
      </>}
      
    </StoneXMainPage>
  );
}